<template lang="pug">
ValidationObserver(ref="dialogObserver")
  AppDialog.client-dialog(
    :key="show"
    append-to-body
    :loading="showDialogLoading"
    :show="show"
    :title="isNewForm ? 'New client' : 'Edit client'"
    @close="close"
  )
    AppTabs(
      v-if="!showDialogLoading"
      :selected-tab="selectedTab"
      compact
    )
      AppTab(
        label="General"
        tab-id="general"
        :has-error="validateTab('general')"
      )
        ValidationObserver(ref="general")
          ClientDialogTabGeneral
      AppTab(
        label="Contact info"
        tab-id="contact"
        :has-error="validateTab('contact')"
      )
        ValidationObserver(ref="contact")
          ClientDialogTabContact
      AppTab(
        :label="projectTabText"
        tab-id="projects"
        :has-error="validateTab('projects')"
      )
        ValidationObserver(ref="projects")
          ClientDialogTabProjects
      AppTab(
        label="budget"
        tab-id="budget"
        :has-error="validateTab('budget')"
      )
        ValidationObserver(ref="budget")
          ClientDialogTabBudgets(@clearedBudget="setConfirmClearBudget")
      AppTab(
        v-if="invoicesVisible"
        label="invoicing"
        tab-id="invoicing"
        :has-error="validateTab('invoicing')"
      )
        ValidationObserver(ref="invoicing")
          ClientDialogTabInvoicing
    template(slot="footer")
      button.btn.btn-default(@click="close") Cancel
      button.btn.btn-primary(
        :disabled="submitting"
        @click="handleSubmit"
      )
        | {{ !submitting ? 'Save' : 'Saving...' }}
</template>
<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import ClientDialogTabGeneral from './ClientDialogTabGeneral.vue';
import ClientDialogTabContact from './ClientDialogTabContact.vue';
import ClientDialogTabProjects from './ClientDialogTabProjects.vue';
import ClientDialogTabBudgets from './ClientDialogTabBudgets.vue';
import ClientDialogTabInvoicing from './ClientDialogTabInvoicing.vue';
import { handleBudget } from '~/utils/budget_utils';
import { handleFormErrors } from '~/utils/form_utils';
import { getProjectFlavourLabel } from '~/utils/project_flavour';

export default {
  name: 'ClientDialog',
  components: {
    ClientDialogTabGeneral,
    ClientDialogTabContact,
    ClientDialogTabProjects,
    ClientDialogTabBudgets,
    ClientDialogTabInvoicing,
  },
  data() {
    return {
      show: false,
      selectedTab: '',
      confirmClearBudget: false,
      reloadTimeoutId: null,
    };
  },
  computed: {
    ...mapState(['organization', 'client', 'isNewForm', 'submitting', 'errors', 'budget']),
    ...mapGetters(['clientData', 'invoicesVisible', 'projectsVisible', 'workOrdersVisible', 'showDialogLoading']),
    projectTabText() {
      if (this.projectsVisible && this.workOrdersVisible) {
        // This is a special case where we cannot use the same label
        // in order to avoid long names in dialog tabs.
        return 'Projects / WO';
      }
      if (this.workOrdersVisible) {
        return getProjectFlavourLabel('work_orders', true);
      }

      return getProjectFlavourLabel('projects', true);
    },
  },
  beforeDestroy() {
    this.clearReloadTimeout();
  },
  methods: {
    ...mapActions([
      'loadInitialState',
      'setFormType',
      'createClient',
      'updateClient',
      'setSubmitting',
      'resetFields',
      'updateField',
      'resetBudget',
    ]),
    open(attr) {
      this.loadData(attr);
      this.show = true;
    },
    close() {
      this.show = false;
      this.confirmClearBudget = false;
      this.resetFields();
    },
    loadData(attr) {
      this.setFormType(attr.newForm);
      this.loadInitialState(attr.initialState);
      this.selectedTab = attr.selectedTab ? attr.selectedTab : 'general';
    },
    validateTab(tabId) {
      if (this.$refs.dialogObserver && this.$refs.dialogObserver.flags.invalid) {
        const tab = this.$refs[tabId];

        return tab && tab.flags.validated && tab.flags.invalid;
      }
    },
    async handleSubmit() {
      const validated = await this.$refs.dialogObserver.validate();

      if (validated) {
        this.checkNotes();
        await this.setSubmitting(true);
        if (this.isNewForm) {
          this.checkDetails(this.clientData);
          await this.createClient(this.clientData).then(this.completeRequest, this.handleErrors);
        } else {
          if (this.confirmClearBudget && this.client.budget && !this.client.budget.type) {
            if (confirm("Removing this client's budget will remove its budget history. Are you sure?")) {
              this.updateField({ path: 'budget', value: null });
              await this.updateClient(this.clientData).then(this.completeRequest, this.handleErrors);
            } else {
              this.setSubmitting(false);
            }
          } else {
            this.checkDetails(this.clientData);
            await this.updateClient(this.clientData).then(this.completeRequest, this.handleErrors);
          }
        }
      }
    },
    completeRequest(response) {
      if (response.success) {
        this.handleSuccess();
      } else {
        this.handleErrors();
      }
    },
    handleSuccess() {
      let message;

      if (this.isNewForm) {
        message = 'Client created';
      } else {
        message = 'Client updated';
      }
      this.$appMessage(message);
      const theWindow = window;

      this.reloadTimeoutId = setTimeout(() => theWindow.location.reload(), 600);
    },
    handleErrors() {
      handleFormErrors(this.$refs.dialogObserver, this.errors, this.$appMessage);
      if (!this.client.budget) {
        this.resetBudget();
      }
    },
    setConfirmClearBudget() {
      this.confirmClearBudget = true;
    },
    checkDetails(clientData) {
      this.checkBudget(clientData);
      this.checkInvoice();
      this.checkPhone();
    },
    checkBudget(clientData) {
      const budget = this.client.budget;
      const clientDataCheck = clientData.budget;

      if (!handleBudget(this.budget, clientDataCheck, budget)) {
        delete clientData.budget;
      }
    },
    checkInvoice() {
      if (this.client.use_organization_net_terms && this.client.net_terms) {
        this.updateField({ path: 'net_terms', value: null });
      }
      if (this.client.use_organization_notes && this.client.invoice_notes) {
        this.updateField({ path: 'invoice_notes', value: null });
      }
      if (this.client.use_organization_tax_rate && this.client.tax_1) {
        this.updateField({ path: 'tax_1', value: null });
      }
    },
    checkPhone() {
      if (this.client.phone) {
        const phone = this.client.phone;

        this.updateField({ path: 'phone', value: phone.replace(/[- ]/g, '') });
      }
    },
    checkNotes() {
      if (!this.client.use_organization_notes && this.client.invoice_notes === null) {
        this.updateField({ path: 'invoice_notes', value: '' });
      }
    },
    clearReloadTimeout() {
      clearTimeout(this.reloadTimeoutId);
      this.reloadTimeoutId = null;
    },
  },
};
</script>
